import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo, useCallback, useEffect } from 'react';

import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { MANUAL_TRACKING_OR_NO_TRACKING, OBJECT_OF_PLAY } from '@/shared/hooks/useServerState';

import { AutoIsoObjectType } from '../../autoIso/types';
import { Tracking } from '@/shared/services/tracking';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';

// Constants for toggle values
const MANUAL_MODE = 'manual';
const AUTO_ISO_MODE = 'auto-iso';

export const AutoIsoSelect = ({ sx }: { sx?: object }) => {
  // Server State Context
  const { trackEntity, selectedObjectType } = useServerStateContext();
  const { game } = useCurrentGame();
  const trackingDisabled = game.enableTracking === false;

  // When tracking is disabled, force the server state to Manual so all downstream
  // consumers (team selector, PTZ controls) reflect Manual mode, not just the toggle.
  useEffect(() => {
    if (trackingDisabled && selectedObjectType !== AutoIsoObjectType.None) {
      trackEntity(MANUAL_TRACKING_OR_NO_TRACKING, 'Tracking Disabled');
    }
  }, [trackingDisabled, selectedObjectType, trackEntity]);

  // Normalize the selected value: 'manual' if None, 'auto-iso' otherwise
  const selectedMode = useMemo(() => {
    return selectedObjectType === AutoIsoObjectType.None ? MANUAL_MODE : AUTO_ISO_MODE;
  }, [selectedObjectType]);

  // Memoize handlers to prevent re-renders
  const handleManualClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      Tracking.getInstance().track('Manual Mode', {
        category: 'Controls',
        source: 'Toggle Button',
      });
      trackEntity(MANUAL_TRACKING_OR_NO_TRACKING, 'Toggle Button');
    },
    [trackEntity],
  );

  const handleAutoIsoClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      Tracking.getInstance().track('Auto-ISO Mode', {
        category: 'Controls',
        source: 'Toggle Button',
      });
      trackEntity(OBJECT_OF_PLAY, 'Toggle Button');
    },
    [trackEntity],
  );

  return (
      <Stack direction="row" sx={[sx ?? {}, { alignItems: 'center' }]}>
        <ToggleButtonGroup color="primary" value={selectedMode} exclusive size="small" fullWidth>
          <ToggleButton value={MANUAL_MODE} onClick={handleManualClick} size="small">
            Manual
          </ToggleButton>

          <ToggleButton
            value={AUTO_ISO_MODE}
            onClick={handleAutoIsoClick}
            disabled={trackingDisabled}
            sx={{
              '&.Mui-disabled': {
                color: (theme) => theme.palette.text.disabled,
                backgroundColor: 'transparent',
              },
            }}
          >
            Auto-ISO
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

  );
};


export default AutoIsoSelect;
